import React, { useMemo } from 'react';

import { formatCurrency } from '~libs/dataUtils';
import { getErrorMessage } from '~libs/axiosWrapper';

import { Button, ClickableIcon, ErrorResponse, Modal, Spinner } from '~components';
import Icon, { faArrowLeft, faRefresh } from '~components/icon';
import { TableView } from '~components/table-view';

import ClientInvoicesModal from '~direct-debits/components/payment-batches/ClientInvoicesModal';
import PaymentStatusBadge from '~direct-debits/components/payment-batches/PaymentStatusBadge';
import { BatchClientRow, columnsFactory } from '~direct-debits/forms/payment-batches/batchDetail';

import { useGetConnectionsQuery } from '~direct-debits/actions/connections';
import { useGetPaymentBatchDetailQuery } from '~direct-debits/actions/paymentBatches';
import { PaymentBatchClient } from '~direct-debits/models';

interface ComponentProps {
  batchId: string;
  onBack: () => void;
  onViewInvoices: (clientId: number) => void;
  // Client whose invoices are open in the stacked dialog, or null for none.
  selectedClientId?: number | null;
  onCloseInvoices?: () => void;
  onViewInvoice?: (invoiceId: string) => void;
  onRefund?: (row: PaymentBatchClient) => void;
  onReverse?: (row: PaymentBatchClient) => void;
}

const HEADER_ID = 'dd-payment-batch-detail-header-label';

const HeaderRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="d-flex flex-column me-4">
    <span className="text-muted small">{label}</span>
    <span className="fw-semibold">{value}</span>
  </div>
);

const PaymentBatchDetail: React.FC<ComponentProps> = ({
  batchId,
  onBack,
  onViewInvoices,
  selectedClientId = null,
  onCloseInvoices,
  onViewInvoice,
  onRefund,
  onReverse,
}) => {
  const { data, isFetching, isError, error, refetch } = useGetPaymentBatchDetailQuery({ batch_id: batchId });
  const { data: connections, isFetching: isFetchingConnections } = useGetConnectionsQuery();

  // Environment for the "Open in GoCardless" deep-links — only ever the active
  // connection. We deliberately do NOT fall back to an arbitrary connections[0]:
  // an old/disconnected sandbox row could send a live payment to a sandbox link
  // (or vice versa), the exact mis-host gocardlessPaymentUrl refuses to guess.
  // With no active connection the environment is null and the link is hidden.
  const environment = useMemo(
    () => connections?.find((connection) => connection.status === 'active')?.environment ?? null,
    [connections],
  );

  const rows = useMemo<BatchClientRow[]>(
    () =>
      (data?.clients ?? []).map((client) => ({
        ...client,
        // TableView requires `id` — fall back to client_id which is unique
        // within the batch.
        id: client.client_id,
      })),
    [data?.clients],
  );

  const columns = useMemo(
    () => columnsFactory({ onViewInvoices, onRefund, onReverse, environment }),
    [onViewInvoices, onRefund, onReverse, environment],
  );

  const selectedClientName = data?.clients.find((client) => client.client_id === selectedClientId)?.client_name;

  // A batch opens as a wide dialog over the list so it reads as drilling into
  // detail rather than replacing the page. Closing by either route the shared
  // Modal offers - the header X or the footer button - goes back to the list.
  // (That wrapper does not forward reactstrap's `toggle`, so backdrop clicks
  // and Escape are inert here, as they are for every dialog in the app.)
  const setIsOpen = (open: boolean) => {
    if (!open) onBack();
  };

  return (
    <Modal
      isOpen
      setIsOpen={setIsOpen}
      size="xl"
      scrollable
      header={<span id={HEADER_ID}>{data?.reference ? `Payment Batch ${data.reference}` : 'Payment Batch'}</span>}
      headerClassName="header"
      labelledBy={HEADER_ID}
      footer={
        <Button
          id="dd-payment-batch-detail-back"
          data-cy="dd-payment-batch-detail-back"
          color="secondary"
          onClick={onBack}
        >
          <Icon icon={faArrowLeft} className="me-2" />
          Back to Batches
        </Button>
      }
      data-cy="dd-payment-batch-detail"
    >
      <div className="dd-payment-batch-detail" data-cy="dd-payment-batch-detail-body">
        {isError && <ErrorResponse message={getErrorMessage(error)} />}
        {isFetching && !data && (
          <div className="my-3">
            <Spinner loading />
          </div>
        )}
        {data && (
          <>
            <div
              className="d-flex flex-wrap align-items-center mb-3 p-2 border rounded"
              data-cy="dd-payment-batch-detail-header"
            >
              <HeaderRow label="Batch ID" value={data.reference} />
              <HeaderRow label="Status" value={<PaymentStatusBadge status={data.status} />} />
              <HeaderRow label="Total Amount" value={formatCurrency(data.total_amount)} />
              <HeaderRow label="Clients" value={data.client_count} />
              <ClickableIcon
                className="ms-auto"
                icon={faRefresh}
                id="dd-payment-batch-detail-refresh-button"
                onClick={() => refetch()}
                disabled={isFetching}
                toolTip="Refresh"
                ariaLabel="Refresh batch detail"
                dataCy="dd-payment-batch-detail-refresh-button"
              />
            </div>
            <TableView
              id="dd-payment-batch-clients-table"
              columns={columns}
              data={rows}
              isLoading={isFetching || isFetchingConnections}
              emptyMessage="No clients in this batch"
              readonly
              striped
            />
          </>
        )}
        {/*
          The invoices open as a dialog stacked over this one so they always
          land in the same place, whichever client row was picked and however
          many clients the batch holds. Reactstrap portals it out to the body,
          so it is a DOM sibling of this dialog and dismissing it leaves this
          one open behind it.
        */}
        <ClientInvoicesModal
          isOpen={selectedClientId !== null}
          batchId={batchId}
          batchReference={data?.reference}
          clientId={selectedClientId}
          clientName={selectedClientName}
          onClose={() => onCloseInvoices?.()}
          onViewInvoice={(invoiceId) => onViewInvoice?.(invoiceId)}
        />
      </div>
    </Modal>
  );
};

export default PaymentBatchDetail;
